import styles from "./BlockVenueCard.module.css"
import { useRef } from 'react';
import { addDPH } from "../../../utils/dph";
import { Users, MapPinned } from 'lucide-react';
import { VenueProductCardProps } from "../../../types/venue.types"
import CartButtonSecondary from "../../Button/CartButton/CartButtonSecondary";
import CartAmountSecondary from "../../Button/CartButton/CartAmountSecondary";
import { useWindowSize } from "../../../hooks/useWindowSize";
import { useCartContext } from "../../../context/CartContext";
import { useDetailContext } from '../../../context/DetailContext';
import { useOrderContext } from "../../../context/OrderContext";
import useTimeModalActions from "../../../hooks/useTimeModalActions";
import { usePreferences } from "../../../context/PreferencesContext";
import { czk_eur_rate } from "../../../constants/prices";
import { czk_to_eur } from "../../../utils/prices";
import { useTranslation } from "react-i18next";
import { Trans } from "react-i18next";
import { normalizeDate, denormalizeDate } from "../../../utils/calendar";
import { usePopup } from "../../../context/PopupContext";
import { usePublicCatalog } from "../../../context/PublicCatalogContext";
import { defaultOrderTime } from "../../../constants/times";
import useCartAmountSync from "../../../hooks/useCartAmountSync";
import useCardClickGuard from "../../../hooks/useCardClickGuard";
/**
* Block card for venues
*
* @param props VenueProductCardProps
* @param {number} props.id - Venue id
* @param {string} props.title - Venue title
* @param {string} props.address - Venue address
* @param {number} props.price - Venue price
* @param {string} props.photo - Venue photo URL
* @param {string} props.type - Venue type
* @param {number} props.amountOfPeople - Recommended amount of people
* @returns TSX.Element
*/
const BlockVenueCard = ({id, title, address, price, photo, type, amountOfPeople} : VenueProductCardProps) => {
    const { cartData, reloadCart, cartLoaded } = useCartContext();
    const { t } = useTranslation(); // Translation
    const { orderData, orderType } = useOrderContext(); // Order data
    const screenWidth = useWindowSize(); // Screen width
    const { openDetail } = useDetailContext(); // Detail context
    const { openTimeModal } = useTimeModalActions(); // Time modal
    const { selectedCurrency } = usePreferences(); // Current selected currency
    const { open: openPopup, close: closePopup, setPopupTarget } = usePopup();
    const {isPublicView} = usePublicCatalog();

    const dateKey = denormalizeDate(orderData.selectedDate);
    const dateISO = normalizeDate(orderData.selectedDate);
    const { cartAmount, handleCart } = useCartAmountSync({
        cartData,
        cartLoaded,
        reloadCart,
        itemKey: {
            id,
            category: "venue",
            dateKey,
            dateISO,
        },
    });

    const buttonRef = useRef<HTMLDivElement>(null)
    // Handle opening detail
    const handleOpen = useCardClickGuard([buttonRef], () => {
        openDetail && openDetail({id: id, type: "venue" }); 
    });
    // Make sure price is always in correct currency
    const no_dph_price = czk_to_eur(price, czk_eur_rate, selectedCurrency).toFixed(2);
    const dph_price = czk_to_eur(addDPH(price), czk_eur_rate, selectedCurrency).toFixed(2);

    return(
        <>
        <div className={styles.ProductBlock} key={id} onClick={handleOpen}>
                <div className={styles.ProductIImageCenter}>
                    <div className={styles.ProductImage} style={{ backgroundImage: `url(${photo})` }}></div>
                </div>
                <div className={styles.ProductContent}>
                    <div className={styles.ProductDescription}>
                        <p>{title}</p>
                        <span>{address}</span>
                    </div>
                    <div className={styles.ProductInfo}>
                        {screenWidth > 1200 && (
                            <div className={styles.ProductTags}>
                                <div className={styles.ProductTag}>
                                    <MapPinned/>
                                    <p>{type}</p>
                                </div>
                                <div className={styles.ProductTag}>
                                    <Users />
                                    <p>{t("venue.capacity")} {amountOfPeople} {t("products.people")}</p>
                                </div>
                            </div>
                        )}
                        <div className={styles.ProductPrice}>
                            <p className={styles.NoDph}>{no_dph_price} { selectedCurrency === "eur"? "EUR" : "Kč"}/<Trans i18nKey="products.no_dph" components={{ 1: <span/> }} /></p>
                            <p className={styles.WithDph}>{dph_price} { selectedCurrency === "eur"? "EUR" : "Kč"} {t("products.dph")}</p>
                        </div>
                    </div>
                </div>
                <div className={styles.CartButtons} ref={buttonRef}>
                    {cartAmount>0 ? (
                        <CartAmountSecondary amountInCart={cartAmount} 
                        onIncrease={() => handleCart(cartAmount + 1)}
                        onDecrease={() => handleCart(Math.max(cartAmount - 1, 0))}
                        disabled={isPublicView}
                        className={styles.CartAmount}/>
                    ) : (
                        <CartButtonSecondary text={t("buttons.demand")} 
                            onClick={()=>{
                                if(orderType !== "demand"){
                                    setPopupTarget(
                                            {
                                                title: t("demand_popup.title"),
                                                content: t("demand_popup.content"),
                                                submitBtnText: t("demand_popup.continue"),
                                                backBtnText: t("demand_popup.back"),
                                                onClose: (()=>{closePopup()}),
                                                onSubmit: (()=>{
                                                    openTimeModal({
                                                        type: "single",
                                                        date: orderData.selectedDate,
                                                        time: defaultOrderTime,
                                                        productId: id,
                                                        prodType: "venue",
                                                        static: true,
                                                    });
                                                    closePopup();
                                                })
                                            }
                                        )
                                    openPopup()
                                }else{
                                    openTimeModal({
                                        type: "single",
                                        date: orderData.selectedDate,
                                        time: defaultOrderTime,
                                        productId: id,
                                        prodType: "venue",
                                        static: true,
                                    });
                                }
                            }}

                            className={styles.CartButton}
                            disabled={isPublicView}
                            />
                    )}
                </div>
            </div>
        </>
    )
}
export default BlockVenueCard
